/**
 * Pure field patches for a twin-test gate questions artifact.
 *
 * All edits go through `twinTestGateQuestionAdapter` so parse / serialize /
 * type-switch memory stay one contract. Mirrors ConfirmRunCard's commit-on-blur
 * model: each helper takes the staged body, mutates one field, and returns a
 * new body in the same fence wrapper.
 *
 * Cache scope: one-shot, not cached (local JSON surgery, no LLM).
 */

import { WorkflowQuickStudyStepConfig, WorkflowTemplate, optionPlaceholderLabel } from '@digital-twins/shared-types'

import {
  applyGateQuestionTypeSwitch,
  blankGateEditableQuestion,
  GateEditableColumn,
  GateEditableQuestion,
  GateEditableRow,
  GateTypeMemory,
  GateTypeStash,
  readTwinTestGateTypedBody,
  twinTestGateQuestionAdapter,
  migrateSharedScaleAnchorsToColumns,
  TWIN_TEST_GATE_DEFAULT_RATING_SPAN,
  TWIN_TEST_GATE_QUESTION_TYPES,
  typeNeedsColumns,
} from './twinTestGateAdapter'

/**
 * Fallback peer-question ceiling (D5, platform-wide cap of 2) for a run whose
 * template did not declare one, or declared none at all. Mirrors backend
 * `TWIN_TEST_MAX_QUESTIONS`. Prefer `twinTestGateMaxQuestions`, which reads
 * the declared max off the run's own template instead of this mirror.
 */
export const TWIN_TEST_MAX_QUESTIONS = 2

/**
 * The peer-question ceiling this gate should enforce: the template's own
 * `quick_study` step `maxQuestions`, not a hand-kept mirror of the backend
 * constant. Falls back to `TWIN_TEST_MAX_QUESTIONS` when the template is not
 * loaded yet or declares no `quick_study` step. Same shape as
 * `audienceInputKey` in `composeRunStudyDoc.ts`: one small pure reader so the
 * gate and any other caller read the same declaration.
 */
export function twinTestGateMaxQuestions(
  template: WorkflowTemplate | null | undefined,
): number {
  const quickStudyStep = template?.steps.find(
    (step): step is WorkflowQuickStudyStepConfig => step.kind === 'quick_study',
  )
  return quickStudyStep?.maxQuestions ?? TWIN_TEST_MAX_QUESTIONS
}

export {
  migrateSharedScaleAnchorsToColumns,
  TWIN_TEST_GATE_QUESTION_TYPES,
  twinTestGateQuestionAdapter,
  typeNeedsColumns,
}
export type {
  GateEditableColumn,
  GateEditableQuestion,
  GateEditableRow,
  GateTypeMemory,
  GateTypeStash,
}

function withQuestions(
  body: string,
  mutate: (questions: Array<GateEditableQuestion>) => Array<GateEditableQuestion> | null,
): string {
  if (!readTwinTestGateTypedBody(body)) {
    return body
  }
  const questions = twinTestGateQuestionAdapter.parse(body)
  if (!questions) {
    return body
  }
  const next = mutate(questions)
  if (!next) {
    return body
  }
  return twinTestGateQuestionAdapter.serialize(next, body)
}

/** Peer question count (no follow-up / asked-unit accounting). */
export function countGateQuestions(body: string): number {
  const typed = readTwinTestGateTypedBody(body)
  return typed ? typed.entries.length : 0
}

/** Cap at `maxQuestions` peer questions (default: `TWIN_TEST_MAX_QUESTIONS`). */
export function canAddGateQuestion(
  body: string,
  maxQuestions: number = TWIN_TEST_MAX_QUESTIONS,
): boolean {
  return countGateQuestions(body) < maxQuestions
}

/**
 * Append a blank question of `questionType` (default `open_end`). Unchanged
 * body when at or over `maxQuestions` (default: `TWIN_TEST_MAX_QUESTIONS`).
 * Works from a valid empty list.
 */
export function addGateQuestion(
  body: string,
  maxQuestions: number = TWIN_TEST_MAX_QUESTIONS,
  questionType: string = 'open_end',
  language?: string | null,
): string {
  return withQuestions(body, (questions) => {
    if (questions.length >= maxQuestions) {
      return null
    }
    return [...questions, blankGateEditableQuestion(questionType, language)]
  })
}

/**
 * Drop one peer question. Empty list is allowed (Approve stays disabled until
 * the reviewer adds at least one back).
 */
export function removeGateQuestion(
  body: string,
  questionIndex: number,
): string {
  return withQuestions(body, (questions) => {
    if (questionIndex < 0 || questionIndex >= questions.length) {
      return null
    }
    return questions.filter((_question, index) => index !== questionIndex)
  })
}

export function setGateQuestionPrompt(
  body: string,
  questionIndex: number,
  prompt: string,
): string {
  const next = prompt.trim()
  if (!next) {
    return body
  }
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question) {
      return null
    }
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, prompt: next }
    return nextQuestions
  })
}

export function setGateQuestionTitle(
  body: string,
  questionIndex: number,
  title: string,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question) {
      return null
    }
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, title: title.trim() }
    return nextQuestions
  })
}

export function setGateQuestionChoice(
  body: string,
  questionIndex: number,
  choiceIndex: number,
  value: string,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question) {
      return null
    }
    if (choiceIndex < 0 || choiceIndex >= question.choices.length) {
      return null
    }
    const next = value.trim()
    if (!next) {
      return null
    }
    const choices = [...question.choices]
    choices[choiceIndex] = next
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, choices }
    return nextQuestions
  })
}

export function addGateQuestionChoice(
  body: string,
  questionIndex: number,
  language?: string | null,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question) {
      return null
    }
    const nextLabel = optionPlaceholderLabel(
      language ?? undefined,
      question.choices.length + 1,
    )
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = {
      ...question,
      choices: [...question.choices, nextLabel],
    }
    return nextQuestions
  })
}

/** Remove a choice; refuses when fewer than 2 would remain. */
export function removeGateQuestionChoice(
  body: string,
  questionIndex: number,
  choiceIndex: number,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question) {
      return null
    }
    if (question.choices.length <= 2) {
      return null
    }
    if (choiceIndex < 0 || choiceIndex >= question.choices.length) {
      return null
    }
    const choices = question.choices.filter(
      (_choice, index) => index !== choiceIndex,
    )
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, choices }
    return nextQuestions
  })
}

export function setGateQuestionScaleLabel(
  body: string,
  questionIndex: number,
  which: 'minLabel' | 'maxLabel',
  label: string,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question?.scale) {
      return null
    }
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = {
      ...question,
      scale: {
        ...question.scale,
        [which]: label.trim(),
      },
    }
    return nextQuestions
  })
}

export function setGateQuestionScaleSpan(
  body: string,
  questionIndex: number,
  min: number,
  max: number,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question?.scale) {
      return null
    }
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
      return null
    }
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = {
      ...question,
      scale: {
        ...question.scale,
        min,
        max,
      },
    }
    return nextQuestions
  })
}

/**
 * Change question type. `memory` is mutated (studies-like stash per prior type).
 * Returns the new body; memory map is updated in place by the caller-owned Map.
 */
export function setGateQuestionType(
  body: string,
  questionIndex: number,
  nextType: string,
  memory: GateTypeMemory,
  language?: string | null,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question) {
      return null
    }
    if (question.questionType === nextType) {
      return null
    }
    if (!TWIN_TEST_GATE_QUESTION_TYPES.includes(nextType)) {
      return null
    }

    const result = applyGateQuestionTypeSwitch(
      question,
      nextType,
      memory.get(questionIndex),
      language,
    )
    memory.set(questionIndex, result.memory)

    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = result.question
    return nextQuestions
  })
}

/** Default rating span for blank rating cards (shared with adapter). */
export { TWIN_TEST_GATE_DEFAULT_RATING_SPAN }

function columnId(index: number): string {
  return index < 26 ? `c${String.fromCharCode(97 + index)}` : `col_${index + 1}`
}

function rowId(index: number): string {
  return index < 26 ? `r${String.fromCharCode(97 + index)}` : `row_${index + 1}`
}

export function setGateQuestionRowLabel(
  body: string,
  questionIndex: number,
  rowIndex: number,
  label: string,
): string {
  const next = label.trim()
  if (!next) {
    return body
  }
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question?.rows || rowIndex < 0 || rowIndex >= question.rows.length) {
      return null
    }
    const rows = question.rows.map((row, index) =>
      index === rowIndex ? { ...row, label: next } : row,
    )
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, rows }
    return nextQuestions
  })
}

export function addGateQuestionRow(
  body: string,
  questionIndex: number,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question || !typeNeedsColumns(question.questionType)) {
      return null
    }
    const existing = question.rows ?? []
    const rows: Array<GateEditableRow> = [
      ...existing,
      {
        id: rowId(existing.length),
        label: `Row ${existing.length + 1}`,
      },
    ]
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, rows }
    return nextQuestions
  })
}

/** Remove a row; refuses when fewer than 1 would remain. */
export function removeGateQuestionRow(
  body: string,
  questionIndex: number,
  rowIndex: number,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question?.rows || question.rows.length <= 1) {
      return null
    }
    if (rowIndex < 0 || rowIndex >= question.rows.length) {
      return null
    }
    const rows = question.rows.filter((_row, index) => index !== rowIndex)
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, rows }
    return nextQuestions
  })
}

export function setGateQuestionColumnLabel(
  body: string,
  questionIndex: number,
  columnIndex: number,
  label: string,
): string {
  const next = label.trim()
  if (!next) {
    return body
  }
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (
      !question?.columns ||
      columnIndex < 0 ||
      columnIndex >= question.columns.length
    ) {
      return null
    }
    const columns = question.columns.map((column, index) =>
      index === columnIndex ? { ...column, label: next } : column,
    )
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, columns }
    return nextQuestions
  })
}

export function setGateQuestionColumnScaleLabel(
  body: string,
  questionIndex: number,
  columnIndex: number,
  end: 'min' | 'max',
  value: string,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (
      !question?.columns ||
      columnIndex < 0 ||
      columnIndex >= question.columns.length
    ) {
      return null
    }
    const trimmed = value.trim()
    const stored = trimmed.length > 0 ? trimmed : null
    const key = end === 'min' ? 'minLabel' : 'maxLabel'
    const columns = question.columns.map((column, index) =>
      index === columnIndex ? { ...column, [key]: stored } : column,
    )
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, columns }
    return nextQuestions
  })
}

export function setGateQuestionColumnSpan(
  body: string,
  questionIndex: number,
  columnIndex: number,
  min: number,
  max: number,
): string {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return body
  }
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (
      !question?.columns ||
      columnIndex < 0 ||
      columnIndex >= question.columns.length
    ) {
      return null
    }
    const columns = question.columns.map((column, index) =>
      index === columnIndex ? { ...column, min, max } : column,
    )
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = migrateSharedScaleAnchorsToColumns({
      ...question,
      columns,
    })
    return nextQuestions
  })
}

export function addGateQuestionColumn(
  body: string,
  questionIndex: number,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question || !typeNeedsColumns(question.questionType)) {
      return null
    }
    const span = TWIN_TEST_GATE_DEFAULT_RATING_SPAN
    const isCategorical = question.questionType === 'matrix_categorical'
    const migrated = migrateSharedScaleAnchorsToColumns(question)
    const existing = migrated.columns ?? []
    const columns: Array<GateEditableColumn> = [
      ...existing,
      {
        id: columnId(existing.length),
        label: isCategorical
          ? `Category ${existing.length + 1}`
          : `Measure ${existing.length + 1}`,
        min: isCategorical ? null : span.min,
        max: isCategorical ? null : span.max,
        minLabel: null,
        maxLabel: null,
      },
    ]
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...migrated, columns }
    return nextQuestions
  })
}

/** Remove a column; refuses when fewer than 1 (rating) or 2 (categorical) remain. */
export function removeGateQuestionColumn(
  body: string,
  questionIndex: number,
  columnIndex: number,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question?.columns) {
      return null
    }
    const minColumns =
      question.questionType === 'matrix_categorical' ? 2 : 1
    if (question.columns.length <= minColumns) {
      return null
    }
    if (columnIndex < 0 || columnIndex >= question.columns.length) {
      return null
    }
    const columns = question.columns.filter(
      (_column, index) => index !== columnIndex,
    )
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, columns, scale: null }
    return nextQuestions
  })
}
